import { useRef, useState, useEffect } from 'react'
import { getHotelRatesByIds } from '../config/api'
import { slugifyHotelName } from '../utils/hotelUrl'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { getSiteSettings } from '../utils/siteSettings'
import { optimizeHotelThumbnail } from '../utils/imageOptimization'
import { getDisplayPricingFromRate } from '../utils/pricing'
import './FeaturedMarriottHawaii.css'

const MARRIOTT_LOGO = 'https://cdn.brandfetch.io/id0DQ-cAhI/theme/dark/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B'

// Hotel data - deeplinks are generated dynamically with dates 1 week from now
const FEATURED_HOTELS = [
  {
    hotelId: 'lp3b0be',
    name: 'The Ritz-Carlton Maui, Kapalua',
    location: 'Maui',
    thumbnail: 'https://static.cupid.travel/hotels/427864413.jpg',
  },
  {
    hotelId: 'lp22779',
    name: 'The Royal Hawaiian, A Luxury Collection Resort, Waikiki',
    location: 'Oahu',
    thumbnail: 'https://static.cupid.travel/hotels/470657116.jpg',
  },
  {
    hotelId: 'lp19d41',
    name: 'Sheraton Waikiki',
    location: 'Oahu',
    thumbnail: 'https://static.cupid.travel/hotels/ex_8fa11a6b_z.jpg',
  },
  {
    hotelId: 'lp1c872',
    name: "The Westin Maui Resort & Spa, Ka'anapali",
    location: 'Maui',
    thumbnail: 'https://static.cupid.travel/hotels/ex_ab741e7f_z.jpg',
  },
  {
    hotelId: 'lp2565b',
    name: 'Wailea Beach Resort - Marriott, Maui',
    location: 'Maui',
    thumbnail: 'https://static.cupid.travel/hotels/427478895.jpg',
  },
  {
    hotelId: 'lp1a86c',
    name: 'The Westin Hapuna Beach Resort',
    location: 'Big Island',
    thumbnail: 'https://static.cupid.travel/hotels/426582167.jpg',
  },
  {
    hotelId: 'lp207c8',
    name: 'Sheraton Kauai Coconut Beach Resort',
    location: 'Kauai',
    thumbnail: 'https://static.cupid.travel/hotels/480989471.jpg',
  },
  {
    hotelId: 'lp20eb8',
    name: 'Alohilani Resort Waikiki Beach',
    location: 'Oahu',
    thumbnail: 'https://static.cupid.travel/hotels/141330098.jpg',
  }
]

const FEATURED_STAY_NIGHTS = 2

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getFeaturedStayDates() {
  const checkinDate = new Date()
  checkinDate.setDate(checkinDate.getDate() + 7)

  const checkoutDate = new Date(checkinDate)
  checkoutDate.setDate(checkoutDate.getDate() + FEATURED_STAY_NIGHTS)

  return {
    checkin: formatDate(checkinDate),
    checkout: formatDate(checkoutDate),
  }
}

function getRateEntries(response) {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.hotels)) return response.data.hotels
  if (Array.isArray(response?.hotels)) return response.hotels
  if (Array.isArray(response?.rates)) return response.rates
  return []
}

function getHotelIdFromRateEntry(entry) {
  return entry?.hotelId || entry?.id || entry?.hotel?.hotelId || entry?.hotel?.id || entry?.hotelData?.id
}

function getFeaturedRatePrices(response, currency) {
  return getRateEntries(response).reduce((prices, entry) => {
    const hotelId = getHotelIdFromRateEntry(entry)
    const roomType = entry?.roomTypes?.[0]
    const rate = roomType?.rates?.[0] || entry?.rates?.[0]
    if (!hotelId || !rate) return prices

    const pricing = getDisplayPricingFromRate({
      rate,
      roomType,
      nights: FEATURED_STAY_NIGHTS,
      commissionFallback: entry?.commission?.[0]?.amount || entry?.commission || 0,
    })

    if (!pricing?.nightlyRate || pricing.nightlyRate <= 0) return prices

    prices[hotelId] = {
      amount: Math.round(pricing.nightlyRate),
      currency,
    }

    return prices
  }, {})
}

function formatPriceBadge({ amount, currency }) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Generate a deeplink with dynamic dates (1 week from now, 2 night stay)
 */
function generateDeeplink(hotel) {
  const { language, currency } = getSiteSettings()
  const { checkin, checkout } = getFeaturedStayDates()
  
  // Base64 encoded occupancies: [{"adults":2,"children":[]}]
  const occupancies = 'W3siYWR1bHRzIjoyLCJjaGlsZHJlbiI6W119XQ=='
  
  const params = new URLSearchParams({
    language,
    currency,
    source: 'direct',
    checkin,
    checkout,
    rooms: '1',
    adults: '2',
    children: '',
    occupancies
  })
  
  return `/hotel/${hotel.hotelId}/${slugifyHotelName(hotel.name)}?${params.toString()}`
}

function FeaturedMarriottHawaii() {
  const carouselRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  const [pricesByHotelId, setPricesByHotelId] = useState({})

  // Drag state
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollStart = useRef(0)
  const didDrag = useRef(false)

  const checkScrollButtons = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
      setCanScrollLeft(scrollLeft > 5)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5)
    }
  }

  useEffect(() => {
    checkScrollButtons()
    const carousel = carouselRef.current
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollButtons)
      window.addEventListener('resize', checkScrollButtons)
      return () => {
        carousel.removeEventListener('scroll', checkScrollButtons)
        window.removeEventListener('resize', checkScrollButtons)
      }
    }
  }, [])

  const scroll = (direction) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.querySelector('.featured-marriott-card')?.offsetWidth || 280
      const gap = 20
      const scrollAmount = (cardWidth + gap) * 2
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  // Drag-to-scroll handlers
  const handleMouseDown = (e) => {
    isDragging.current = true
    didDrag.current = false
    startX.current = e.pageX
    scrollStart.current = carouselRef.current?.scrollLeft || 0
    if (carouselRef.current) {
      carouselRef.current.style.scrollSnapType = 'none'
      carouselRef.current.style.scrollBehavior = 'auto'
    }
  }

  const handleMouseMove = (e) => {
    if (!isDragging.current || !carouselRef.current) return
    const dx = e.pageX - startX.current
    if (Math.abs(dx) > 5) {
      didDrag.current = true
      carouselRef.current.scrollLeft = scrollStart.current - dx
    }
  }

  const handleMouseUp = () => {
    isDragging.current = false
    if (carouselRef.current) {
      carouselRef.current.style.scrollSnapType = 'x mandatory'
      carouselRef.current.style.scrollBehavior = ''
    }
  }

  const handleClick = (e, hotel) => {
    if (didDrag.current) {
      e.preventDefault()
    } else {
      window.open(generateDeeplink(hotel), '_blank', 'noopener,noreferrer')
    }
  }

  // Attach global mouse events for drag
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  useEffect(() => {
    let active = true

    async function fetchFeaturedRates() {
      try {
        const { currency = 'USD' } = getSiteSettings()
        const { checkin, checkout } = getFeaturedStayDates()
        const response = await getHotelRatesByIds({
          hotelIds: FEATURED_HOTELS.map((hotel) => hotel.hotelId),
          checkin,
          checkout,
          adults: 2,
          rooms: 1,
          currency,
        })

        if (!active) return
        setPricesByHotelId(getFeaturedRatePrices(response, currency))
      } catch (error) {
        console.warn('[FeaturedMarriottHawaii] Rates unavailable:', error)
      }
    }

    fetchFeaturedRates()

    return () => {
      active = false
    }
  }, [])

  return (
    <section className="featured-marriott-hawaii">
      <div className="container">
        <div className="featured-marriott-header">
          <h2>Featured Marriott Hotels in Hawaii</h2>
        </div>

        <div className="featured-marriott-carousel-wrapper">
          {canScrollLeft && (
            <button 
              className="carousel-nav-btn carousel-nav-left"
              onClick={() => scroll('left')}
              aria-label="Scroll left"
            >
              <FiChevronLeft size={24} />
            </button>
          )}

          <div 
            className="featured-marriott-carousel" 
            ref={carouselRef}
            onMouseDown={handleMouseDown}
          >
            {FEATURED_HOTELS.map((hotel) => {
              const price = pricesByHotelId[hotel.hotelId]

              return (
              <div
                key={hotel.hotelId}
                className="featured-marriott-card"
                onClick={(e) => handleClick(e, hotel)}
                style={{ cursor: 'pointer' }}
              >
                <div className="featured-marriott-image">
                  <img 
                    src={optimizeHotelThumbnail(hotel.thumbnail)} 
                    alt={hotel.name} 
                    loading="lazy" 
                    draggable={false}
                    width="220"
                    height="147"
                  />
                  {price && (
                    <span className="featured-marriott-price-badge">
                      From {formatPriceBadge(price)}
                    </span>
                  )}
                </div>
                <div className="featured-marriott-info">
                  <h3>{hotel.name}</h3>
                  <span className="featured-marriott-location">{hotel.location}</span>
                </div>
              </div>
              )
            })}
          </div>

          {canScrollRight && (
            <button 
              className="carousel-nav-btn carousel-nav-right"
              onClick={() => scroll('right')}
              aria-label="Scroll right"
            >
              <FiChevronRight size={24} />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

export default FeaturedMarriottHawaii
